import { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Grid } from '@mui/material';

import { AppLayout } from '@/components/layout/AppLayout';
import { LlmCostAthenaCard } from '@/components/dashboard-llm-cost/LlmCostAthenaCard';
import { LlmCostBreakdownTabs } from '@/components/dashboard-llm-cost/LlmCostBreakdownTabs';
import {
  LlmCostFilterBar,
  type LlmCostFilterValue,
} from '@/components/dashboard-llm-cost/LlmCostFilterBar';
import { LlmCostJobsTable } from '@/components/dashboard-llm-cost/LlmCostJobsTable';
import { LlmCostRunningPanel } from '@/components/dashboard-llm-cost/LlmCostRunningPanel';
import { LlmCostSummaryCards } from '@/components/dashboard-llm-cost/LlmCostSummaryCards';
import {
  LlmCostCostChart,
  LlmCostCountChart,
} from '@/components/dashboard-llm-cost/LlmCostTrendChart';
import { formatDate } from '@/components/dashboard-llm-cost/format';
import { useAuth } from '@/hooks/useAuth';
import { useDashboardLlmCost } from '@/lib/query/hooks/useDashboardLlmCost';

/** 粒度は期間の長さから自動決定する（短期=日 / 中期=週 / 長期=月）。 */
export function granularityFor(startDate: string, endDate: string): 'day' | 'week' | 'month' {
  const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000;
  if (days <= 60) return 'day';
  if (days <= 365) return 'week';
  return 'month';
}

/**
 * custom-dashboard Agent の LLM コスト・運用確認ビュー（T-0042）。
 * サイドバー「ダッシュボード」>「LLMコスト」。account 横断。
 */
export function DashboardLlmCostPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // 既定: 過去 30 日
  const defaults = useMemo<LlmCostFilterValue>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
      accountCode: '',
      tabId: '',
      status: '',
    };
  }, []);
  const [filter, setFilter] = useState<LlmCostFilterValue>(defaults);
  const granularity = granularityFor(filter.startDate, filter.endDate);

  const {
    data,
    isLoading: dataLoading,
    error: dataError,
    dataUpdatedAt,
  } = useDashboardLlmCost({
    start_date: filter.startDate,
    end_date: filter.endDate,
    granularity,
    account_code: filter.accountCode || undefined,
    tab_id: filter.tabId || undefined,
    status: filter.status || undefined,
  });
  // 実行中パネルの経過時間は取得時刻を基準にする（再取得のたびに進む）。
  const now = useMemo(() => new Date(dataUpdatedAt || Date.now()), [dataUpdatedAt]);

  if (authLoading) {
    return (
      <AppLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <AppLayout>
        <Alert severity="warning">この機能を利用するにはログインが必要です。</Alert>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="custom-dashboard Agent LLMコスト">
      <Box sx={{ p: 3 }}>
        <LlmCostFilterBar
          value={filter}
          accountOptions={data?.accountOptions ?? []}
          tabOptions={data?.tabOptions ?? []}
          onChange={setFilter}
        />

        {dataLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {dataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            LLMコストの取得に失敗しました。
          </Alert>
        )}

        {data && (
          <Grid container spacing={3}>
            <Grid size={12}>
              <LlmCostSummaryCards summary={data.summary} />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <LlmCostCostChart buckets={data.buckets} />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <LlmCostCountChart buckets={data.buckets} />
            </Grid>
            <Grid size={12}>
              <LlmCostAthenaCard athena={data.athena} />
            </Grid>
            <Grid size={12}>
              <LlmCostRunningPanel jobs={data.running} now={now} />
            </Grid>
            <Grid size={12}>
              <LlmCostBreakdownTabs byAccount={data.byAccount} byTab={data.byTab} />
            </Grid>
            <Grid size={12}>
              <LlmCostJobsTable jobs={data.jobs} />
            </Grid>
          </Grid>
        )}
      </Box>
    </AppLayout>
  );
}
